import React, { useState, useEffect, useCallback } from "react";
import CustomModal from "../../Common/CustomModal";
import { FormControlLabel } from "@mui/material";
import { East } from "@mui/icons-material";
import { checklistData } from "../../../assets/data/data";
import axiosInstance from "../../../utils/config/config";
import {
  MODAL_TITLE,
  SAVE_BTN_TEXT,
  CANCEL_BTN_TEXT,
  API_ENDPOINT,
  ERROR_MESSAGES,
  INTRO_TEXT,
} from "./SuccessChecklistModal.constants";
import {
  ContentBox,
  SectionTitle,
  ItemWrapper,
  StyledCheckbox,
  StyledCheckboxIcon,
  StyledCheckboxCheckedIcon,
  LabelText,
  StyledList,
  StyledListItem,
  BulletText,
  SubList,
  SubListItem,
  SubBulletText,
} from "./SuccessChecklistModal.styles";
import {
  buildInitialChecklist,
  transformApiResponseToChecklist,
} from "./SuccessChecklistModal.utils";

const SuccessChecklistModal = ({
  open,
  handleClose,
  setLoading,
  setSuccess,
  setErrorState,
}) => {
  const [checkedItems, setCheckedItems] = useState(() =>
    buildInitialChecklist(checklistData)
  );

  const fetchChecklist = useCallback(async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINT);
      if (response?.data) {
        const data = response.data.marketingChecklist;
        setCheckedItems(transformApiResponseToChecklist(data));
      }
    } catch (error) {
      console.error("Error fetching checklist", error);
    }
  }, []);

  useEffect(() => {
    if (open) fetchChecklist();
  }, [open, fetchChecklist]);

  const handleCheck = (value) => {
    setCheckedItems((prev) => ({
      ...prev,
      [value]: { ...prev[value], checked: !prev[value]?.checked },
    }));
  };

  const handleSave = async () => {
    setErrorState({ error: false, message: "" });

    const payload = Object.entries(checkedItems).map(([key, value]) => ({
      value: key,
      id: value.id,
      checked: value.checked,
    }));

    try {
      setLoading(true);
      const response = await axiosInstance.post(API_ENDPOINT, payload);

      if (response.status === 200) {
        setSuccess(true);
        setErrorState({ error: false, message: "" });
        handleClose();
        fetchChecklist();
      }
    } catch (error) {
      console.error("Error saving checklist", error);
      setErrorState({ error: true, message: ERROR_MESSAGES.saveFailed });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCheckedItems((prev) =>
      Object.keys(prev).reduce(
        (acc, key) => ({ ...acc, [key]: { ...prev[key], checked: false } }),
        {}
      )
    );
  };

  const hasAnyChecked = Object.values(checkedItems).some(
    (item) => item?.checked
  );
  const dataList = checklistData ?? [];

  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      title={MODAL_TITLE}
      saveBtnText={SAVE_BTN_TEXT}
      cancelBtnText={CANCEL_BTN_TEXT}
      disableBtn={!hasAnyChecked}
      saveIcon={East}
      onSave={handleSave}
      onCancel={handleReset}
    >
      <ContentBox>
        <SectionTitle>{INTRO_TEXT}</SectionTitle>

        {dataList.map((item) => (
          <ItemWrapper key={item.id}>
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={checkedItems[item.value]?.checked ?? false}
                  onChange={() => handleCheck(item.value)}
                  icon={<StyledCheckboxIcon />}
                  checkedIcon={<StyledCheckboxCheckedIcon />}
                />
              }
              label={<LabelText>{item.title}</LabelText>}
            />
            {item.bulletPoints?.length > 0 && (
              <StyledList>
                {item.bulletPoints.map((point, index) => (
                  <StyledListItem
                    key={`${item.id}-${index}-${point.text ?? ""}`}
                  >
                    <BulletText>• {point.text}</BulletText>
                    {point.subPoints?.length > 0 && (
                      <SubList>
                        {point.subPoints.map((subpoint, subIndex) => (
                          <SubListItem key={`${item.id}-${index}-${subIndex}`}>
                            <SubBulletText>○ {subpoint}</SubBulletText>
                          </SubListItem>
                        ))}
                      </SubList>
                    )}
                  </StyledListItem>
                ))}
              </StyledList>
            )}
          </ItemWrapper>
        ))}
      </ContentBox>
    </CustomModal>
  );
};

export default SuccessChecklistModal;
